import { PermissionSuffix } from '../../_generated/api/user-management'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Tree,
    TreeDragDropEvent,
    TreeExpandedEvent,
    TreeNodeClickEvent,
    TreeTogglerTemplateOptions,
} from 'primereact/tree'
import { TreeNode } from 'primereact/treenode'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { Menu } from 'primereact/menu'

import { useStructureContext } from '../contexts/StructureContext'
import { useUserContext } from '../contexts/UserContext'
import StructureNode from './StructureNode'
import PrimeIcon, { pi } from '../common/pure/PrimeIcon'
import LoadingCloak from '../common/pure/LoadingCloak'
import DirectoryEntry from '../../models/DirectoryEntry'
import { StructureType } from '../../models/Structure'
import { GenericStorage } from '../../tools/dom/storage'
import t from '../../tools/t'
import { Column, Row } from '../common/pure/FlexContainer'
import Heading from '../common/pure/Heading'
import {
    useArchiveEntryAndNavigate,
    useCreateUnnamedEntryAndNavigate,
    useCreateUnnamedRootEntryAndNavigate,
} from '../../hooks/crud'
import MenuBuilder from '../../tools/MenuBuilder'

const expandedStorage = new GenericStorage<Record<string, boolean>>('EXPANDED_STORAGE')

export default function Structure() {
    const navigate = useNavigate()
    const ref = useRef<Menu>(null)
    const { loading, structure, moveStructure, duplicateStructure } = useStructureContext()
    const { can } = useUserContext()
    const createUnnamedAndNavigate = useCreateUnnamedEntryAndNavigate()
    const createRootAndNavigate = useCreateUnnamedRootEntryAndNavigate()
    const archiveAndNavigate = useArchiveEntryAndNavigate()
    const canWriteStructure = can(PermissionSuffix.StructureWrite)
    const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>(
        expandedStorage.read() ?? {}
    )
    const [enableDragDrop, setEnableDragDrop] = useState(false)

    const mapStructure = (entry: DirectoryEntry): TreeNode => ({
        key: entry.id,
        data: entry,
        label: entry.name,
        draggable: !entry.isRoot,
        children: entry.children.map(mapStructure),
    })

    const nodes = structure.map(mapStructure)

    const onNodeClick = (event: TreeNodeClickEvent) => {
        const node = event.node.data as DirectoryEntry
        if (!node) return
        navigate(node.referenceLink)
    }

    const onNodeDoubleClick = (event: TreeNodeClickEvent) => {
        const node = event.node.data as DirectoryEntry
        const key = node.id
        const current = expandedKeys[key]
        if (current) delete expandedKeys[key]
        else expandedKeys[key] = true
        setExpandedKeys({ ...expandedKeys })
    }

    const onCreateRootNode = async () => {
        if (!canWriteStructure) return
        await createRootAndNavigate()
    }

    const onArchiveNode = async (node: TreeNode) => {
        if (!canWriteStructure) return
        const entry = node?.data as DirectoryEntry
        await archiveAndNavigate(entry, entry.referenceType)
    }

    const onDuplicateNode = (node: TreeNode) => {
        if (!canWriteStructure) return
        const entry = node.data as DirectoryEntry
        duplicateStructure(entry)
    }

    const onAddChildNode = async (node: TreeNode, type: StructureType) => {
        if (!canWriteStructure) return
        const parent = node?.data as DirectoryEntry
        if (!parent) return
        await createUnnamedAndNavigate(parent, type)
        toggleNodeExpanded(node, true)
    }

    const onToggleExpanded = (event: TreeExpandedEvent) => {
        const expanded = event.value
        expandedStorage.store(expanded)
        setExpandedKeys(expanded)
    }

    const toggleNodeExpanded = (node: TreeNode, expanded?: boolean) => {
        const entry = node.data as DirectoryEntry
        expandedKeys[entry.ancestorIdPath] =
            typeof expanded === 'boolean' ? expanded : !expandedKeys[entry.ancestorIdPath]
        expandedStorage.store(expandedKeys)
        setExpandedKeys(expandedKeys)
    }

    const onDragDrop = async ({ dropIndex, dragNode, dropNode }: TreeDragDropEvent) => {
        if (!canWriteStructure) return
        const entry = dragNode.data as DirectoryEntry
        // The dropNode can actually be `null` unlike what the type definition says.
        // This happens when reordering top-level nodes.
        const destinationEntry = dropNode?.data as DirectoryEntry
        // In cases where the drop happens on top of a node, the dropIndex represents that parent's index,
        //  which will basically always be an incorrect destination index for the moved node.
        // Instead, we look through the new list of children to find where in the parent it was placed internally by the tree itself.
        const destinationChildren = dropNode?.children ?? []
        const index = !destinationEntry
            ? dropIndex
            : destinationChildren.findIndex(
                  (child) => (child.data as DirectoryEntry).id === entry.id
              )

        const previousSibling = index > 0 ? destinationChildren[index - 1] : undefined
        const previousSiblingEntry = previousSibling?.data as DirectoryEntry | undefined
        const data = {
            parentId: destinationEntry.id,
            placeAfterOrderKey: previousSiblingEntry?.orderKey ?? null,
        }
        await moveStructure(entry, data)
    }

    const toggleDragDrop = () => setEnableDragDrop(!enableDragDrop)
    const checkOr = (state: boolean, icon: PrimeIconName) => (state ? pi('check') : pi(icon))

    const menu = new MenuBuilder()
        .addIf(
            {
                label: t('structure_node_reorder'),
                command: toggleDragDrop,
                icon: checkOr(enableDragDrop, 'sort'),
            },
            canWriteStructure
        )
        .toArray()

    return (
        <Column wide gap={0.5} alignment="start" className="sub-menu">
            <Row wide arrangement="spaced" gap={0.5}>
                <Heading size={3}>{t('common_projects')}</Heading>
                {menu.length > 0 && (
                    <>
                        <Button
                            onClick={ref.current?.toggle}
                            icon={pi('ellipsis-v')}
                            severity="secondary"
                            rounded
                            outlined
                            text
                        />
                        <Menu model={menu} ref={ref} popup />
                    </>
                )}
            </Row>
            <Tree
                emptyMessage={t('structure_empty')}
                onNodeClick={onNodeClick}
                onNodeDoubleClick={onNodeDoubleClick}
                onToggle={onToggleExpanded}
                value={nodes}
                expandedKeys={expandedKeys}
                dragdropScope={enableDragDrop && canWriteStructure ? 'structure' : undefined}
                onDragDrop={canWriteStructure ? onDragDrop : undefined}
                togglerTemplate={(node, options) => (
                    // NOTE: Always render the structure toggle to fill the space it requires
                    // The toggler will only be shown for nodes that have children.
                    <StructureToggler node={node} options={options} />
                )}
                nodeTemplate={(node, options) => (
                    <StructureNode
                        node={node}
                        options={options}
                        onAddChildNode={onAddChildNode}
                        onDuplicateNode={onDuplicateNode}
                        onDeleteNode={onArchiveNode}
                    />
                )}
            />
            <Divider />
            {/* NOTE: This div lets the button keep its natural height in the flowing column
                instead of collapsing its height to its content. */}
            <div>
                {canWriteStructure && (
                    <Button
                        text
                        onClick={onCreateRootNode}
                        label={t('project_create')}
                        icon={pi('plus')}
                        severity="secondary"
                    />
                )}
            </div>
            <LoadingCloak loading={loading} />
        </Column>
    )
}

interface StructureTogglerProps {
    node: TreeNode
    options: TreeTogglerTemplateOptions
}
const StructureToggler = ({ node, options }: StructureTogglerProps) => {
    const children = node.children?.length ?? 0
    return (
        <div className="structure-node-toggler" onClick={options.onClick}>
            {children > 0 && (
                <PrimeIcon size={9} name={options.expanded ? 'chevron-down' : 'chevron-left'} />
            )}
        </div>
    )
}
